import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send, Loader2, Mail, Paperclip, CalendarDays, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMULE_LABELS = {
    economique: 'Economique',
    economique_plus: 'Economique plus',
    standard: 'Standard',
    luxe: 'Luxe',
};

// Ordre fixe pour le tri des formules dans le message email :
// Economique -> Economique plus -> Standard -> Luxe
const FORMULE_ORDER = ['economique', 'economique_plus', 'standard', 'luxe'];

const TYPE_VISITE_MAP = {
    visite_presentielle: 'visite technique',
    visite_virtuelle: 'visite virtuelle',
    listing_email: 'échange par email',
    listing_telephone: 'échange téléphonique',
    devis_estimatif: 'demande de devis',
};

const buildDefaultMessage = ({ civilite, clientNom, typeVisite, formules, commercialName, commercialTel, nbDevis = 1 }) => {
    // Tri des formules dans l'ordre Economique, Economique plus, Standard, Luxe
    const formulesSorted = [...formules].sort((a, b) => {
        const ia = FORMULE_ORDER.indexOf(a);
        const ib = FORMULE_ORDER.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    const formulesLabels = formulesSorted.map(f => FORMULE_LABELS[f] || f);
    // Construit "formule X" si 1, "formules X et Y" si 2, "formules X, Y et Z" si 3+
    let formulesText = '';
    if (formulesLabels.length === 1) {
        formulesText = `formule ${formulesLabels[0]}`;
    } else if (formulesLabels.length > 1) {
        const last = formulesLabels[formulesLabels.length - 1];
        const rest = formulesLabels.slice(0, -1).join(', ');
        formulesText = `formules ${rest} et ${last}`;
    }
    const visiteText = TYPE_VISITE_MAP[typeVisite] || 'visite technique';
    const plural = nbDevis > 1;
    const devisMot = plural ? 'vos devis' : 'votre devis';
    const detailleMot = plural ? 'détaillés' : 'détaillé';
    const devisConvient = plural ? 'nos devis vous conviendront' : 'notre devis vous conviendra';

    return `Bonjour ${civilite} ${clientNom},

Suite à notre ${visiteText} et comme convenu, veuillez trouver ci-joint ${devisMot} de déménagement ${detailleMot}${formulesText ? ` en ${formulesText}` : ''}.

Nous tenons à vous informer que tous vos objets seront entièrement assurés par notre assurance Allianz tout au long du déménagement, y compris pendant les phases de chargement, de transport, de déchargement et de manutention.

Nous espérons que ${devisConvient} et que vous choisirez BIG DÉMÉNAGEMENT pour votre prochain déménagement. Si vous avez des questions ou des demandes supplémentaires, n'hésitez pas à nous contacter. Nous sommes là pour vous aider.

Nous vous remercions encore pour votre confiance et nous vous souhaitons une excellente journée.

Bien cordialement,
${commercialName || 'Big Déménagement'}
Big Déménagement
${commercialTel || '01 84 20 04 93'}`;
};

export function SendDevisEmailDialog({ open, onClose, devisList, clientNom, clientEmail, inventaireId, inventaire }) {
    const activeDevis = (devisList || []).filter(d => d.est_actif);
    const [selectedIds, setSelectedIds] = useState([]);
    const [destinataire, setDestinataire] = useState('');
    const [sujet, setSujet] = useState('');
    const [message, setMessage] = useState('');
    const [civilite, setCivilite] = useState('M.');
    const [sending, setSending] = useState(false);
    const [dateRelance1, setDateRelance1] = useState('');
    const [remarqueRelance, setRemarqueRelance] = useState('');
    const [attachAssurance, setAttachAssurance] = useState(false);
    const [attachPresentation, setAttachPresentation] = useState(false);
    const [hasAssuranceFile, setHasAssuranceFile] = useState(false);
    // Pieces jointes uploadees depuis l'appareil
    const [extraFiles, setExtraFiles] = useState([]);
    const extraFileInputRef = useRef(null);
    const MAX_TOTAL_BYTES = 25 * 1024 * 1024; // 25 Mo (limite Gmail)

    useEffect(() => {
        if (open) {
            setSelectedIds(activeDevis.map(d => d.id));
            setDestinataire(clientEmail || '');
            setDateRelance1('');
            setRemarqueRelance('');
            setAttachAssurance(false);
            setAttachPresentation(false);
            setExtraFiles([]);
            // Detect if assurance file is uploaded
            axios.get(`${API}/api/admin/documents/assurance`, { withCredentials: true })
                .then(({ data }) => setHasAssuranceFile(!!(data && data.filename)))
                .catch(() => setHasAssuranceFile(false));
            const formules = [...new Set(activeDevis.map(d => d.formule))];
            setSujet(`Votre devis de déménagement - ${clientNom}`);

            // Get commercial info via the public endpoint (accessible à tous, pas que admin)
            const commercialId = inventaire?.commercial_id || activeDevis[0]?.commercial_id;
            const nbDevis = activeDevis.length;
            if (commercialId) {
                axios.get(`${API}/api/users/${commercialId}/public`, { withCredentials: true })
                    .then(res => {
                        const comm = res.data || {};
                        setMessage(buildDefaultMessage({
                            civilite,
                            clientNom: clientNom || '',
                            typeVisite: inventaire?.type_inventaire || '',
                            formules,
                            commercialName: comm.name || '',
                            commercialTel: comm.telephone || '',
                            nbDevis,
                        }));
                    })
                    .catch(() => {
                        setMessage(buildDefaultMessage({
                            civilite,
                            clientNom: clientNom || '',
                            typeVisite: inventaire?.type_inventaire || '',
                            formules,
                            commercialName: '',
                            commercialTel: '',
                            nbDevis,
                        }));
                    });
            } else {
                setMessage(buildDefaultMessage({
                    civilite,
                    clientNom: clientNom || '',
                    typeVisite: inventaire?.type_inventaire || '',
                    formules,
                    commercialName: '',
                    commercialTel: '',
                    nbDevis,
                }));
            }
        }
    }, [open]);

    // Regenerate message when civilite changes
    const handleCiviliteChange = (val) => {
        setCivilite(val);
        setMessage(prev => {
            const old = prev.match(/^Bonjour (M\.|Mme)/);
            if (old) return prev.replace(/^Bonjour (M\.|Mme)/, `Bonjour ${val}`);
            return prev;
        });
    };

    const toggleDevis = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleExtraFilesChange = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;
        const all = [...extraFiles, ...incoming];
        const totalBytes = all.reduce((s, f) => s + f.size, 0);
        if (totalBytes > MAX_TOTAL_BYTES) {
            toast.error(`Taille totale > 25 Mo (limite Gmail). Actuelle : ${(totalBytes / 1024 / 1024).toFixed(1)} Mo`);
            e.target.value = '';
            return;
        }
        setExtraFiles(all);
        e.target.value = ''; // permet de réuploader le même fichier ensuite
    };

    const removeExtraFile = (idx) => {
        setExtraFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const formatBytes = (b) => {
        if (b < 1024) return `${b} o`;
        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
        return `${(b / 1024 / 1024).toFixed(1)} Mo`;
    };

    const handleSend = async () => {
        if (!destinataire) { toast.error("Veuillez saisir l'email du destinataire"); return; }
        if (selectedIds.length === 0) { toast.error("Veuillez selectionner au moins un devis"); return; }
        if (!dateRelance1) { toast.error("Veuillez programmer la date de relance 1"); return; }
        setSending(true);
        try {
            let res;
            if (extraFiles.length > 0) {
                // Envoi en multipart pour inclure les fichiers de l'utilisateur
                const fd = new FormData();
                fd.append('devis_ids', JSON.stringify(selectedIds));
                fd.append('destinataire', destinataire);
                fd.append('sujet', sujet);
                fd.append('message', message);
                if (dateRelance1) fd.append('date_relance_1', dateRelance1);
                if (remarqueRelance) fd.append('remarque_relance', remarqueRelance);
                fd.append('attach_assurance', String(attachAssurance));
                fd.append('attach_presentation', String(attachPresentation));
                for (const f of extraFiles) fd.append('extra_files', f, f.name);
                res = await axios.post(`${API}/api/devis/send-email-with-files`, fd, {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                res = await axios.post(`${API}/api/devis/send-email`, {
                    devis_ids: selectedIds,
                    destinataire,
                    sujet,
                    message,
                    date_relance_1: dateRelance1,
                    remarque_relance: remarqueRelance || '',
                    attach_assurance: attachAssurance,
                    attach_presentation: attachPresentation,
                }, { withCredentials: true });
            }
            toast.success(res.data.message || 'Email envoye avec succes');
            onClose(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={() => onClose(false)}>
            <DialogContent className="max-w-2xl w-full max-h-[90dvh] flex flex-col" data-testid="send-email-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <Mail size={20} className="text-[#2e7cd6]" />
                        Envoyer les devis par email
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-5 py-2">
                    {/* Devis selection */}
                    <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                            <Paperclip size={14} className="inline mr-1.5 text-slate-400" />
                            Devis a joindre ({selectedIds.length}/{activeDevis.length})
                        </label>
                        <div className="space-y-2 max-h-[160px] overflow-y-auto border border-slate-200 rounded-lg p-3">
                            {activeDevis.length === 0 ? (
                                <p className="text-sm text-slate-400 italic">Aucun devis actif</p>
                            ) : activeDevis.map(d => (
                                <label
                                    key={d.id}
                                    className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                                    data-testid={`email-devis-check-${d.id}`}
                                >
                                    <Checkbox
                                        checked={selectedIds.includes(d.id)}
                                        onCheckedChange={() => toggleDevis(d.id)}
                                    />
                                    <div className="flex-1 flex items-center gap-2">
                                        <span className="font-mono text-sm text-slate-700">{d.numero_devis}</span>
                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                            {FORMULE_LABELS[d.formule] || d.formule}
                                        </Badge>
                                        {d.prix_final != null && (
                                            <span className="text-sm font-medium text-slate-600 ml-auto">{d.prix_final.toFixed(2)} EUR</span>
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Civilite + Destinataire */}
                    <div className="grid grid-cols-[120px_1fr] gap-3">
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Civilite</label>
                            <Select value={civilite} onValueChange={handleCiviliteChange}>
                                <SelectTrigger data-testid="email-civilite">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="M.">M.</SelectItem>
                                    <SelectItem value="Mme">Mme</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Destinataire</label>
                            <Input
                                type="email"
                                value={destinataire}
                                onChange={e => setDestinataire(e.target.value)}
                                placeholder="email@client.com"
                                data-testid="email-destinataire"
                            />
                        </div>
                    </div>

                    {/* Sujet */}
                    <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Sujet</label>
                        <Input
                            value={sujet}
                            onChange={e => setSujet(e.target.value)}
                            placeholder="Objet de l'email"
                            data-testid="email-sujet"
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
                        <Textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            rows={8}
                            className="resize-y text-sm"
                            data-testid="email-message"
                        />
                    </div>

                    {/* Pièces jointes supplémentaires (documents admin) */}
                    <div className="border border-slate-200 bg-slate-50/60 rounded-lg p-4">
                        <label className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                            <Paperclip size={14} className="text-slate-500" />
                            Joindre des documents complémentaires
                        </label>
                        <div className="space-y-2">
                            <label
                                className={`flex items-center gap-3 p-2 rounded-md cursor-pointer ${hasAssuranceFile ? 'hover:bg-white' : 'opacity-50 cursor-not-allowed'}`}
                                data-testid="attach-assurance-row"
                            >
                                <Checkbox
                                    checked={attachAssurance}
                                    onCheckedChange={(v) => setAttachAssurance(!!v)}
                                    disabled={!hasAssuranceFile}
                                    data-testid="attach-assurance-check"
                                />
                                <span className="text-sm text-slate-700">
                                    Attestation d'assurance (PDF)
                                    {!hasAssuranceFile && <span className="text-xs text-slate-400 ml-2">— non configurée</span>}
                                </span>
                            </label>
                            <label
                                className="flex items-center gap-3 p-2 rounded-md hover:bg-white cursor-pointer"
                                data-testid="attach-presentation-row"
                            >
                                <Checkbox
                                    checked={attachPresentation}
                                    onCheckedChange={(v) => setAttachPresentation(!!v)}
                                    data-testid="attach-presentation-check"
                                />
                                <span className="text-sm text-slate-700">
                                    Présentation de l'entreprise (PDF)
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Pièces jointes uploadées depuis l'appareil de l'utilisateur */}
                    <div className="border border-slate-200 bg-slate-50/60 rounded-lg p-4">
                        <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <Upload size={14} className="text-slate-500" />
                            Joindre des fichiers depuis mon appareil
                        </label>
                        <p className="text-xs text-slate-500 mb-3">
                            PDF, images, documents... (limite totale 25 Mo — Gmail)
                        </p>

                        <input
                            ref={extraFileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleExtraFilesChange}
                            data-testid="extra-files-input"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => extraFileInputRef.current?.click()}
                            className="border-dashed border-slate-300 text-slate-600 hover:bg-white"
                            data-testid="extra-files-pick-btn"
                        >
                            <Upload size={14} className="mr-1.5" />
                            Choisir des fichiers
                        </Button>

                        {extraFiles.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                                {extraFiles.map((f, i) => (
                                    <li
                                        key={i}
                                        className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
                                        data-testid={`extra-file-item-${i}`}
                                    >
                                        <Paperclip size={13} className="text-slate-400 shrink-0" />
                                        <span className="flex-1 truncate text-slate-700">{f.name}</span>
                                        <span className="text-xs text-slate-400 shrink-0">{formatBytes(f.size)}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeExtraFile(i)}
                                            className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0"
                                            title="Retirer"
                                            data-testid={`extra-file-remove-${i}`}
                                        >
                                            <X size={14} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Programmer relance 1 */}
                    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4">
                        <label className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                            <CalendarDays size={16} className="text-amber-600" />
                            Programmer la relance 1 <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-amber-600 mb-2">Si le devis est toujours au statut "Envoyé" à cette date, il sera automatiquement signalé à relancer.</p>
                        <Input
                            type="date"
                            value={dateRelance1}
                            onChange={e => setDateRelance1(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-[200px] text-sm"
                            data-testid="email-date-relance-1"
                        />
                        <div className="mt-3">
                            <label className="text-xs text-amber-700 mb-1 block">Remarque (optionnel)</label>
                            <Input
                                value={remarqueRelance}
                                onChange={e => setRemarqueRelance(e.target.value)}
                                placeholder="Ex: Rappeler le client par téléphone..."
                                className="text-sm"
                                data-testid="email-remarque-relance"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t">
                    <p className="text-xs text-slate-400">
                        {selectedIds.length + (attachAssurance ? 1 : 0) + (attachPresentation ? 1 : 0) + extraFiles.length} pièce{(selectedIds.length + (attachAssurance ? 1 : 0) + (attachPresentation ? 1 : 0) + extraFiles.length) > 1 ? 's' : ''} jointe{(selectedIds.length + (attachAssurance ? 1 : 0) + (attachPresentation ? 1 : 0) + extraFiles.length) > 1 ? 's' : ''}
                    </p>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onClose(false)} data-testid="email-cancel-btn">
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSend}
                            disabled={sending || selectedIds.length === 0 || !destinataire || !dateRelance1}
                            className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                            data-testid="email-send-btn"
                        >
                            {sending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                            {sending ? 'Envoi en cours...' : 'Envoyer'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
